import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, Modal } from 'react-daisyui';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Megaphone from '../../../../assets/Megaphone.svg';
import { useModalsContext } from '../../../ModalsProvider/Modals.context';
import { useUserContext } from '../../../UserProvider/User.context';
import { useSubscriptionContext } from '../../Subscription.context';
import { eUserRole } from '../../../UserProvider/User.enums';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button as DesignSystemButton } from '@leadsales/design-system/react';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { tPaymentMethod } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';
import TrialCardPicker from './TrialCardPicker.component';
import { RouterPaths } from '../../../../components/Router/Router.constants';
import moment from 'moment';
import 'moment/dist/locale/es';
import './SubscriptionModals.styles.scss';

const FEEDBACK_OPTIONS = [
  'Faltan automatizaciones',
  'El precio no se ajusta a mi presupuesto',
  'Busco otras funcionalidades',
  'Otra',
];

type Stage = 'initial' | 'feedback' | 'confirmed' | 'cancelled' | 'end';

export default function TrialConfirmationModalComponent(): React.ReactElement | null {
  const { t } = useTranslation('subscription/modals');
  const { subscription, sendTrialConfirmation } = useSubscriptionContext();
  // RET-2428: the saved cards SubscriptionManagerProvider fetches. This modal
  // mounts inside that provider for this reason.
  const {
    paymentMethods,
    paymentMethodsLoadState,
    reloadPaymentMethods,
    trialChargePaymentMethodId,
    toggleAddPaymentMethodModal,
    cardSavedAsDefaultToken,
    cardSavedAsDefaultPaymentMethodId,
    cardSavedWithoutDefaultToken,
    cardSavedWithoutDefaultPaymentMethodId,
  } = useSubscriptionManagerContext();
  const { currentUserRole } = useUserContext();
  const { showTrialConfirmModal, toggleTrialConfirmModal } = useModalsContext();
  const navigate = useNavigate();
  const nextNaturalDays = 1;

  const [stage, setStage] = useState<Stage>('initial');
  const [selected, setSelected] = useState<string>('');
  const [otherText, setOtherText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const isSubmittingRef = useRef(false);
  const [pickedCardId, setPickedCardId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string>('');
  // The card the confirmation was sent with, for «Se cobrará a tu tarjeta».
  const [chargedCard, setChargedCard] = useState<tPaymentMethod | null>(null);
  const isConfirmingRef = useRef(false);
  const seenCardSavedTokenRef = useRef(cardSavedWithoutDefaultToken);
  const seenCardSavedAsDefaultTokenRef = useRef(cardSavedAsDefaultToken);

  // RET-2428 (T-2, D-27): the customer's pick wins; a lone card is the one
  // sent; otherwise the card that paid the trial is preselected. Without it,
  // nothing is chosen. A pick or trial card that is not in the list does not
  // count. Never a card by its position.
  const isSaved = (paymentMethodId: string | null): boolean =>
    paymentMethodId !== null &&
    paymentMethods.some(
      (paymentMethod) => paymentMethod.paymentMethodId === paymentMethodId,
    );
  let chosenCardId: string | null = null;
  if (isSaved(pickedCardId)) {
    chosenCardId = pickedCardId;
  } else if (paymentMethods.length === 1) {
    chosenCardId = paymentMethods[0].paymentMethodId;
  } else if (isSaved(trialChargePaymentMethodId)) {
    chosenCardId = trialChargePaymentMethodId;
  }

  // RET-2428: an empty list means «no saved cards» only once it was read.
  // Before that, or after a failed read, it means «not known», and the
  // confirmation must not go out on it.
  const areCardsLoaded = paymentMethodsLoadState === 'loaded';
  const areCardsLoading = paymentMethodsLoadState === 'loading';
  const didCardsFailToLoad = paymentMethodsLoadState === 'failed';
  const canConfirm =
    areCardsLoaded &&
    !isConfirming &&
    (paymentMethods.length === 0 || chosenCardId !== null);

  // RET-2428: a card saved from this modal's links becomes the chosen one.
  // In a trial billing saves it without making it the default, which moves
  // this token; the id lands in the same batch. The list reload that follows
  // makes the pick count once the card is in it.
  useEffect(() => {
    if (cardSavedWithoutDefaultToken === seenCardSavedTokenRef.current) return;
    seenCardSavedTokenRef.current = cardSavedWithoutDefaultToken;
    if (cardSavedWithoutDefaultPaymentMethodId !== null) {
      setPickedCardId(cardSavedWithoutDefaultPaymentMethodId);
    }
  }, [cardSavedWithoutDefaultToken, cardSavedWithoutDefaultPaymentMethodId]);

  // RET-2428: billing can also make the added card the default. The card just
  // added is still the one the customer chose, so it is selected either way.
  useEffect(() => {
    if (cardSavedAsDefaultToken === seenCardSavedAsDefaultTokenRef.current) {
      return;
    }
    seenCardSavedAsDefaultTokenRef.current = cardSavedAsDefaultToken;
    if (cardSavedAsDefaultPaymentMethodId !== null) {
      setPickedCardId(cardSavedAsDefaultPaymentMethodId);
    }
  }, [cardSavedAsDefaultToken, cardSavedAsDefaultPaymentMethodId]);

  const graceDays = Number(import.meta.env.VITE_CANCELLATION_DAYS) || 5;

  // fechas
  const trialEndDate = useMemo(
    () => (subscription.endDate ? moment(subscription.endDate).toDate() : null),
    [subscription.endDate],
  );
  const confirmationDate = useMemo(
    () =>
      trialEndDate
        ? moment(trialEndDate).add(nextNaturalDays, 'days').toDate()
        : null,
    [trialEndDate, graceDays],
  );

  const formattedTrialEnd = trialEndDate
    ? moment(trialEndDate).locale('es').format('D [de] MMMM')
    : '';
  const formattedConfirmationDate = confirmationDate
    ? moment(confirmationDate).locale('es').format('D [de] MMMM')
    : '';

  const amountToCharge =
    (subscription.subscriptionBaseCost ?? subscription.totalAmount ?? 0) / 100;

  const formattedAmount =
    amountToCharge.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }) +
    ' ' +
    subscription.currency;

  const isOwner = currentUserRole === eUserRole.OWNER;

  // feedback validation
  const otherWordCount = useMemo(
    () =>
      otherText
        .trim()
        .split(/\s+/)
        .filter((w) => w).length,
    [otherText],
  );
  const canSendFeedback =
    selected &&
    (selected !== 'Otra' || (selected === 'Otra' && otherWordCount >= 5));

  useEffect(() => {
    if (trialEndDate && trialEndDate < new Date()) {
      setStage('end');
    }
  }, [trialEndDate]);

  // acciones
  // RET-2428: sends the chosen card. With no saved card the id is empty and
  // billing uses the card that paid the trial.
  const handleConfirm = async () => {
    // A cancellation in flight must not be followed by a confirmation.
    if (!canConfirm || isConfirmingRef.current || isSubmittingRef.current) {
      return;
    }
    const card =
      paymentMethods.find(
        (paymentMethod) => paymentMethod.paymentMethodId === chosenCardId,
      ) ?? null;

    isConfirmingRef.current = true;
    setIsConfirming(true);
    setConfirmError('');
    let wasConfirmed = false;
    try {
      wasConfirmed = await sendTrialConfirmation({
        confirm: 'continue',
        paymentMethodId: card?.paymentMethodId ?? '',
      });
    } catch {
      wasConfirmed = false;
    } finally {
      isConfirmingRef.current = false;
      setIsConfirming(false);
    }
    // Billing did not record the decision: «Confirmamos tu suscripción»
    // would be false. The customer stays here and can try again.
    if (!wasConfirmed) {
      setConfirmError('No pudimos registrar tu decisión. Inténtalo nuevamente.');
      return;
    }
    setChargedCard(card);
    if (trialEndDate && trialEndDate < new Date()) {
      toggleTrialConfirmModal(false);
      navigate(RouterPaths.dashboard);
    } else {
      setStage('confirmed');
    }
  };

  const handleAddCard = () => {
    toggleAddPaymentMethodModal(true);
  };

  const handleSendFeedback = async () => {
    // A confirmation in flight must not be followed by a cancellation.
    if (isSubmittingRef.current || isConfirmingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError('');

    const reason = selected === 'Otra' ? otherText.trim() : selected;
    try {
      const wasConfirmed = await sendTrialConfirmation({
        confirm: 'cancel',
        reason,
      });
      if (wasConfirmed) {
        setStage('cancelled');
      } else {
        setError('No pudimos registrar tu decisión. Inténtalo nuevamente.');
      }
    } catch {
      setError('No pudimos registrar tu decisión. Inténtalo nuevamente.');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCancellationComplete = () => {
    toggleTrialConfirmModal(false);
    navigate(RouterPaths.dashboard);
  };

  const handleBack = () => {
    if (trialEndDate && trialEndDate < new Date()) {
      setStage('end');
    } else {
      setStage('initial');
    }

    setSelected('');
    setOtherText('');
    setError('');
  };

  if (!showTrialConfirmModal || !isOwner) return null;

  // RET-2428: the card section, between the charge sentence and the buttons.
  const cardSection = areCardsLoaded && (
    <div className="mb-4">
      <TrialCardPicker
        paymentMethods={paymentMethods}
        value={chosenCardId}
        onChange={setPickedCardId}
        trialChargePaymentMethodId={trialChargePaymentMethodId}
        onAddCard={handleAddCard}
      />
    </div>
  );

  // RET-2428: shown above «Sí, continuar suscripción» when the saved cards
  // could not be read, with the way to read them again; and when billing did
  // not record the confirmation.
  const continueNotices = (
    <>
      {didCardsFailToLoad && (
        <div
          role="alert"
          className="mb-3 flex flex-col items-center gap-2 text-[var(--ls-text-error)]"
          data-testid="trial-cards-load-failed"
        >
          <p>{t('trialConfirmation.cardsLoadFailed')}</p>
          <DesignSystemButton
            color="tertiary"
            onClick={() => void reloadPaymentMethods()}
          >
            {t('trialConfirmation.cardsLoadRetry')}
          </DesignSystemButton>
        </div>
      )}
      {confirmError && (
        <p role="alert" className="mb-3 text-error">
          {confirmError}
        </p>
      )}
    </>
  );

  const confirmButtonProps = {
    loading: areCardsLoading || isConfirming,
    disabled: !canConfirm,
    'aria-disabled': !canConfirm,
    'aria-busy': areCardsLoading || isConfirming,
  };

  return (
    <Modal open={true} backdrop={true} className="p-6 w-11/12 max-w-[680px]">
      {stage === 'feedback' && (
        <Modal.Header className="justify-normal">
          {stage === 'feedback' && (
            <a
              className="w-6 h-6 cursor-pointer text-gray-600 text-left"
              onClick={handleBack}
            >
              {' '}
              Atras{' '}
            </a>
          )}
        </Modal.Header>
      )}

      {stage !== 'feedback' && (
        <Modal.Header className="TrialConfirmModal__header">
          {stage === 'initial' && <div className="w-6 h-6"></div>}

          {stage !== 'end' && (
            <button
              type="button"
              aria-label={t('trialConfirmation.close')}
              className="flex h-[var(--ls-tap-min)] w-[var(--ls-tap-min)] items-center justify-center text-gray-600 disabled:opacity-50"
              disabled={isConfirming}
              onClick={() => toggleTrialConfirmModal(false)}
            >
              <XMarkIcon aria-hidden="true" className="w-6 h-6" />
            </button>
          )}
        </Modal.Header>
      )}

      <Modal.Body className="p-0 space-y-4">
        {stage === 'initial' && (
          <>
            <div className="text-center mt-2">
              <div className="w-20 h-20 mx-auto mb-3">
                <Megaphone />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                ¿Deseas continuar con tu suscripción?
              </h3>
              <p className="text-gray-700">
                El periodo inicial de suscripción finalizará el{' '}
                <strong>{formattedTrialEnd}</strong>
              </p>
            </div>
            <div className="mx-6 my-4 text-center">
              <p className="text-center mb-4">
                A partir del <strong>{formattedConfirmationDate}</strong> se
                realizará un cobro mensual recurrente de{' '}
                <strong>{formattedAmount}</strong>
              </p>
              {cardSection}
              {continueNotices}
              <Button
                color="primary"
                className="w-full mb-3 max-w-[620px] "
                {...confirmButtonProps}
                onClick={handleConfirm}
              >
                Sí, continuar suscripción
              </Button>
              <Button
                color="ghost"
                className="w-full mb-3  max-w-[620px] "
                disabled={isConfirming}
                onClick={() => setStage('feedback')}
              >
                No quiero continuar
              </Button>
              <div className="text-center">
                <a
                  href="https://help.leadsales.io/es/collections/16269014-legales"
                  target="_blank"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Consultar términos y condiciones de servicio
                </a>
              </div>
            </div>
          </>
        )}

        {stage === 'feedback' && (
          <>
            <div className="flex flex-col items-center space-y-2 mt-4">
              <span className="text-6xl">🥺</span>
              <h2 className="text-2xl font-bold text-center">
                Ayúdanos a mejorar
              </h2>
              <p className="text-center">
                ¿Cuál fue la principal razón para no continuar?
              </p>
            </div>
            <div className="mx-6 my-4">
              <h3 className="text-lg font-medium mb-2">Ayúdanos a mejorar</h3>
              <p className="text-gray-700 mb-4">
                Tu respuesta nos ayuda a mejorar.
              </p>
              {error && <p role="alert" className="text-error">{error}</p>}
              <div className="space-y-3 mb-4">
                {FEEDBACK_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="feedback"
                      value={opt}
                      checked={selected === opt}
                      onChange={() => setSelected(opt)}
                      className="form-radio text-indigo-600"
                    />
                    <span className="text-gray-800">{opt}</span>
                  </label>
                ))}
              </div>
              {selected === 'Otra' && (
                <textarea
                  rows={3}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring"
                  placeholder="Escribe aqui la razón por la que decidiste no continuar."
                />
              )}
            </div>
          </>
        )}

        {stage === 'cancelled' && (
          <div className="text-center py-8 px-6">
            <span className="text-6xl">🥺</span>
            <h3 className="text-2xl font-bold mt-4 mb-2">
              Gracias por probar Leadsales
            </h3>
            <p className="text-gray-700 mb-4">
              Tu suscripción ha sido suspendida exitosamente.
            </p>
            <p className="text-gray-700">
              No realizaremos ningún cobro adicional. Tu acceso estará
              disponible hasta el <strong>{formattedTrialEnd}</strong>.
            </p>
          </div>
        )}

        {stage === 'confirmed' && (
          <div className="text-center py-8 px-6">
            <span className="text-6xl">🎉</span>
            <h3 className="text-2xl font-bold mt-4 mb-2">
              Confirmamos tu suscripción
            </h3>
            <p className="text-gray-700 mb-4">
              Recuerda que tu próximo pago de <strong>{formattedAmount}</strong>{' '}
              se realizará automáticamente el{' '}
              <strong>{formattedConfirmationDate}</strong>
            </p>
            {chargedCard && (
              <p className="text-gray-700 mb-4" data-testid="trial-charged-card">
                <Trans
                  t={t}
                  i18nKey="trialConfirmation.cardPicker.chargedTo"
                  values={{ lastDigits: chargedCard.lastDigits }}
                  components={{ strong: <strong /> }}
                />
              </p>
            )}
            <div>
              <a
                href="https://help.leadsales.io/es/collections/16269014-legales"
                target="_blank"
                className="text-sm text-blue-600 hover:underline"
              >
                Consultar términos y condiciones de servicio
              </a>
            </div>
          </div>
        )}

        {stage === 'end' && (
          <>
            <div className="text-center mt-2">
              <div className="w-20 h-20 mx-auto mb-3">
                <Megaphone />
              </div>
              <h3 className="text-2xl font-bold mb-1">
                El periodo inicial de suscripción ha finalizado
              </h3>
              <p className="text-gray-700 mb-4">
                ¿Deseas continuar con tu suscripción?
              </p>
            </div>
            <div className="mx-4 mb-4 p-6  rounded-lg">
              <ul className="space-y-3 text-gray-800">
                <li>
                  ✨ <strong>Gestión centralizada:</strong> WhatsApp, Instagram
                  y Messenger en un solo lugar
                </li>
                <li>
                  🚀 <strong>Trabajo en equipo sin caos:</strong> Múltiples
                  agentes atendiendo desde un mismo número
                </li>
                <li>
                  🕶️ <strong>Organización visual de leads:</strong> Embudos
                  ilimitados para ventas, soporte o seguimiento
                </li>
                <li>
                  💬 <strong>Atiende mejor, más rápido y sin esfuerzo:</strong>{' '}
                  Mensajes programados y respuestas rápidas
                </li>
                <li>
                  🤖 <strong>Leadbot:</strong> Automatiza el primer contacto y
                  empieza a vender desde el primer mensaje
                </li>
                <li>
                  📈 <strong>Soporte y crecimiento:</strong> Analítica de datos,
                  agenda de eventos y acceso a Academy
                </li>
              </ul>
            </div>
            <div className="mx-6 my-4">
              <p className="text-center mb-4 text-lg">
                Al confirmar tu suscripción se realizará un cobro mensual
                recurrente de <strong>{formattedAmount}</strong>
              </p>
              {cardSection}
              {continueNotices}
              <Button
                color="primary"
                className="w-full mb-3"
                {...confirmButtonProps}
                onClick={handleConfirm}
              >
                Sí, continuar suscripción
              </Button>
              <Button
                color="ghost"
                className="w-full mb-3"
                disabled={isConfirming}
                onClick={() => setStage('feedback')}
              >
                No quiero continuar
              </Button>
              <div className="text-center">
                <a
                  href="https://help.leadsales.io/es/collections/16269014-legales"
                  target="_blank"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Consultar términos y condiciones de servicio
                </a>
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Actions className="p-4">
        {stage === 'feedback' && (
          <Button
            color="primary"
            className="w-full"
            disabled={!canSendFeedback || isSubmitting}
            onClick={handleSendFeedback}
          >
            {isSubmitting ? 'Enviando respuesta' : 'Enviar respuesta'}
          </Button>
        )}
        {stage === 'cancelled' && (
          <Button
            color="primary"
            className="w-full"
            onClick={handleCancellationComplete}
          >
            Volver al inicio
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
